<template>
  <div class="oms-tabs">
    <div
      v-if="panes.length > 1"
      class="oms-tab-btn center"
      :class="{ 'oms-tab-btn-disabled': !canScroll }"
      @click="toLeft"
    >
      <a-icon type="left" />
    </div>
    <div class="oms-tab-container" ref="container">
      <div class="oms-tab-wrapper" ref="wrapper" :style="{ left: scroll + 'px' }">
        <div
          :class="['oms-tab-item', act === item.path ? 'oms-tab-item-act' : '', isHomeTab(item) ? 'oms-tab-item-home' : '']"
          v-for="(item, index) in panes"
          :key="item.path"
          @click="toLink(item)"
        >
          <img
            v-if="isHomeTab(item)"
            :src="act === item.path ? pageTabIndexSelectIcon : pageTabIndexIcon"
            class="oms-tab-item-icon"
          />
          <label class="oms-tab-item-title">{{ item.title }}</label>
          <div
            v-if="!isHomeTab(item)"
            class="oms-tab-item-close center"
            @click.stop="toDel(index, act === item.path)"
          >
            <a-icon type="close" />
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="panes.length > 1"
      class="oms-tab-btn center"
      :class="{ 'oms-tab-btn-disabled': !canScroll }"
      @click="toRight"
    >
      <a-icon type="right" />
    </div>
  </div>
</template>

<script>
import { getSubAppHomeMenu } from '@/utils/utils';

export default {
  props: {
    panes: {
      type: Array,
      default: () => [],
    },
    act: String,
  },
  data() {
    return {
      scroll: 0,
      containerWidth: 0,
      wrapperWidth: 0,
      // 记录上一次页签数量：Vuex 是原地 push 数组，watch 拿到的新旧值是同一引用、
      // 长度也相同，无法据此判断是否"新增"，故自行缓存长度来判断增长。
      prevLen: 0,
      pageTabDeleteIcon: require('@/assets/images/pageTabDelete.png'),
      pageTabIndexIcon: require('@/assets/images/pageTabIndex.png'),
      pageTabIndexSelectIcon: require('@/assets/images/pageTabindexSelect.png'),
    };
  },
  watch: {
    // 页签增删后：新增页签(数量变多)时——新页签总是 push 到末尾(见 store addTab)，
    // 直接滚动到"最后一个"页签，确保新开页签完整可见（不依赖激活态，规避激活态还没切过来的时序问题）；
    // 非新增（如关闭页签）时，保证当前激活页签可见。
    panes(newVal) {
      const len = newVal ? newVal.length : 0;
      const grew = len > this.prevLen;
      this.prevLen = len;
      this.$nextTick(() => {
        if (grew) this.scrollToIndex(len - 1);
        else this.scrollToActive();
      });
    },
    // 切换激活页签时也确保其滚动进可视区（例如通过路由跳到已被滚出视野的页签）。
    act() {
      this.$nextTick(() => {
        this.scrollToActive();
      });
    },
    // 标题变化时重新滚动：新页签先用占位符 "....." 再由 fixTab 异步补成真实名字(变宽)，
    // 此时页签数量没变(panes 长度不变)、只是标题变了，之前算的滚动量按窄标题算就偏小、
    // 激活页签露一半。监听标题串变化，补全后把激活页签重新滚到位。
    titlesSig() {
      this.$nextTick(() => {
        this.scrollToActive();
      });
    },
  },
  computed: {
    // 所有页签标题拼成的签名：任一标题变化(如 fixTab 补全)都会变，用于触发重新滚动
    titlesSig() {
      return this.panes.map((p) => p.title).join('|');
    },
    // 页签宽度是否超出容器：未超出时左右箭头置灰禁用
    canScroll() {
      return this.wrapperWidth > this.containerWidth;
    },
    // 子工程自带首页的路径（无则为空）：该页签与基座首页一样展示首页图标且不可关闭
    subHomePath() {
      const m = getSubAppHomeMenu();
      return m && m.menuPath ? m.menuPath : '';
    },
  },
  mounted() {
    this.prevLen = this.panes ? this.panes.length : 0;
    this.scrollInit();
    this.$nextTick(() => {
      this.scrollToActive();
    });
    // 监听页签内容宽度变化：新开页签的标题是先用占位符 "....." 再由 fixTab 异步补成真实名称，
    // 补全后页签会变宽、内容溢出，但标题变化不会触发 panes 数组的 watch，导致之前算的滚动量失效、
    // 激活页签只露一半。用 ResizeObserver 在内容宽度变化时兜底把激活页签重新滚进可视区。
    // scrollToEl 只在页签被裁时才调整，激活页签已完整可见时不会打扰用户手动滚动。
    if (typeof window !== 'undefined' && window.ResizeObserver && this.$refs.wrapper) {
      this.ro = new window.ResizeObserver(() => {
        this.scrollToActive();
      });
      this.ro.observe(this.$refs.wrapper);
    }
  },
  beforeDestroy() {
    if (this.ro) {
      this.ro.disconnect();
      this.ro = null;
    }
    window.onresize = null;
  },
  methods: {
    // 是否为首页页签：基座首页(/) 或 子工程自带首页
    isHomeTab(item) {
      return item.path === '/' || (!!this.subHomePath && item.path === this.subHomePath);
    },
    scrollInit() {
      this.measure();
      window.onresize = () => {
        this.measure();
        this.scrollToActive();
      };
    },
    // 仅测量容器/内容宽度并把 scroll 夹到合法范围（不再无脑重置为 0，
    // 否则每次增删页签都会跳回最左，导致新开页签被挤到看不见）。
    measure() {
      if (!this.$refs.container || !this.$refs.wrapper) return;
      this.containerWidth = this.$refs.container.offsetWidth;
      this.wrapperWidth = this.$refs.wrapper.scrollWidth;
      this.clampScroll();
    },
    // 把 scroll 夹在 [containerWidth - wrapperWidth, 0]；未溢出时归零。
    clampScroll() {
      if (this.wrapperWidth <= this.containerWidth) {
        this.scroll = 0;
        return;
      }
      const min = this.containerWidth - this.wrapperWidth;
      if (this.scroll > 0) this.scroll = 0;
      else if (this.scroll < min) this.scroll = min;
    },
    // 滚动到当前激活页签
    scrollToActive() {
      const wrapper = this.$refs.wrapper;
      if (!wrapper) return;
      this.scrollToEl(wrapper.querySelector('.oms-tab-item-act'));
    },
    // 滚动到第 index 个页签（用于新开页签滚到末尾）
    scrollToIndex(index) {
      const wrapper = this.$refs.wrapper;
      if (!wrapper) return;
      const items = wrapper.querySelectorAll('.oms-tab-item');
      if (index >= 0 && index < items.length) this.scrollToEl(items[index]);
    },
    // 把指定页签元素滚动进可视区：左侧被裁则对齐左边，右侧被裁则对齐右边，各留 8px 余量。
    // 用 requestAnimationFrame 把测量推迟到浏览器完成布局之后再算——否则新增页签的宽度
    // 可能尚未计入 scrollWidth，导致算出的滚动量/夹紧下限偏小，页签只露出一半。
    scrollToEl(el) {
      window.requestAnimationFrame(() => {
        const container = this.$refs.container;
        const wrapper = this.$refs.wrapper;
        if (!container || !wrapper || !el) return;
        this.containerWidth = container.offsetWidth;
        this.wrapperWidth = wrapper.scrollWidth;
        if (this.wrapperWidth <= this.containerWidth) {
          this.scroll = 0;
          return;
        }
        const left = el.offsetLeft;
        const right = left + el.offsetWidth;
        const margin = 8;
        let scroll = this.scroll;
        if (left + scroll < margin) {
          // 左边被裁：对齐到容器左侧
          scroll = -left + margin;
        } else if (right + scroll > this.containerWidth - margin) {
          // 右边被裁：对齐到容器右侧
          scroll = this.containerWidth - right - margin;
        }
        this.scroll = scroll;
        this.clampScroll();
      });
    },
    toLeft() {
      if (this.wrapperWidth > this.containerWidth) {
        this.scroll += 80;
        if (this.scroll > 0) {
          this.scroll = 0;
        }
      }
    },
    toRight() {
      if (this.wrapperWidth > this.containerWidth) {
        this.scroll -= 80;
        if (this.scroll < this.containerWidth - this.wrapperWidth) {
          this.scroll = this.containerWidth - this.wrapperWidth;
        }
      }
    },
    toLink(item) {
      this.$router.push(item.path);
    },
    toDel(index, isCur) {
      this.$emit("del", { index, isCur });
    },
    delAll() {
      this.$emit("delAll");
    },
  },
};
</script>

<style lang="less" scoped>
@oms-tab: 32px;
@oms-tab-color: #eef3f9;
.center {
  display: flex;
  justify-content: center;
  align-items: center;
}

.oms-tabs {
  display: flex;
  align-items: center;
  user-select: none;
  width: 100%;
  height: @oms-tab;

  .oms-tab-container {
    flex: 1;
    height: @oms-tab;
    overflow: hidden;
    position: relative;

    .oms-tab-wrapper {
      display: flex;
      align-items: center;
      gap: 8px;
      height: 100%;
      transition: 0.3s;
      position: absolute;
      left: 0;
      padding: 0 4px;
    }

    .oms-tab-item {
      box-sizing: border-box;
      height: 24px;
      border: 1px solid #E5EAED;
      border-radius: 4px;
      padding: 0 12px;
      flex-shrink: 0;
      display: flex;
      flex-flow: row nowrap;
      align-items: center;
      position: relative;
      background: #fff;
      color: #606266;
      font-size: 12px;

      .oms-tab-item-icon {
        margin-right: 5px;
        font-size: 13px;
        width: 13px;
        height: 13px;
      }

      .oms-tab-item-title {
        cursor: pointer;
        position: relative;
        left: 0;
        transition: 0.2s;
      }

      .oms-tab-item-close {
        height: 100%;
        font-size: 10px;
        box-sizing: border-box;
        cursor: pointer;
        display: none;
        position: absolute;
        top: 0;
        right: 3px;
      }

      // 选中态
      &.oms-tab-item-act {
        background: rgba(53, 107, 255, 0.1);
        border: 1px solid rgba(53, 107, 255, 0.6);
        color: #356BFF;
      }

      &:hover {
        border-color: var(--themeColor, #3673ff);
        color: var(--themeColor, #3673ff);

        .oms-tab-item-title {
          left: -5px;
        }

        .oms-tab-item-close {
          display: flex;
        }
      }

      // 首页固定项：不偏移、无关闭按钮
      &.oms-tab-item-home:hover .oms-tab-item-title {
        left: 0;
      }
    }

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .oms-tab-btn {
    width: @oms-tab;
    height: @oms-tab;
    cursor: pointer;
    flex-shrink: 0;
    background: #fff;
    color: #222222;

    &:hover {
      color: var(--themeColor, #3673ff);
    }

    // 页签未超出容器时，箭头禁用置灰
    &.oms-tab-btn-disabled {
      color: #ccc;
      cursor: not-allowed;

      &:hover {
        color: #ccc;
      }
    }

    .tab-delete-icon {
      width: 14px;
      height: 14px;
    }
  }

  .handle-btn {
    box-shadow: -5px 0 10px -5px #ccc;
  }
}
</style>
